import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { useUserStore } from "store";
import { getCsrfToken } from "../../utils";
import { useServerSearch } from "../../hooks/useServerSearch";
import DecimalField from "../shared/DecimalField";
import MessagePreviewDialog from "../shared/MessagePreviewDialog";
import VisibilityIcon from "@mui/icons-material/Visibility";

// "Rengashotelli" sub-tab of the Ominaisuudet (Features) settings tab.
// Master enable toggle + replacement thresholds + seasonal campaign templates.
// The parent (OrganizationFeaturesTab) only mounts this when the org is in the
// tire_hotel_beta program, so no internal beta guard is needed here.
const TIRE_HOTEL_FIELDS = [
  "tire_hotel_enabled",
  "tire_hotel_tread_depth_warning",
  "tire_hotel_tread_depth_critical",
  "tire_hotel_dot_age_warning",
  "tire_hotel_dot_age_critical",
  "tire_hotel_capacity_alert_threshold",
  "tire_hotel_campaign_email_subject",
  "tire_hotel_campaign_email_template",
  "tire_hotel_campaign_sms_template",
  "tire_hotel_contact_phone",
  "tire_hotel_swap_billing_mode",
  "tire_hotel_swap_job",
  "tire_hotel_swap_service_package",
  "tire_hotel_label_orientation",
];

function TireHotelSettingsTab({ t, onDirtyChange }) {
  const { currentOrganization, fetchUser } = useUserStore();
  const [organization, setOrganizationRaw] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Campaign preview: the templates on screen, saved or not, rendered for a sample card.
  const loadCampaignPreview = async () => {
    const res = await axios.post(
      `/api/organizations/${currentOrganization.organization_id}/message-preview/`,
      {
        kind: "tire_hotel_campaign",
        draft: {
          tire_hotel_campaign_email_subject: organization?.tire_hotel_campaign_email_subject ?? "",
          tire_hotel_campaign_email_template: organization?.tire_hotel_campaign_email_template ?? "",
          tire_hotel_campaign_sms_template: organization?.tire_hotel_campaign_sms_template ?? "",
        },
      },
      { headers: { "X-Organization-Id": currentOrganization.organization_id } },
    );
    return res.data;
  };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  // Kept as an object: the selected job is often outside the first page of results
  const [swapJob, setSwapJob] = useState(null);
  const [packages, setPackages] = useState([]);
  const { items: jobOptions, autocompleteProps: jobAutocompleteProps } = useServerSearch(
    "/api/jobs/",
    { loadOnMount: true }
  );

  const fetchOrganization = useCallback(async () => {
    if (!currentOrganization?.organization_id) return;
    try {
      setLoading(true);
      const response = await axios.get(
        `/api/organizations/${currentOrganization.organization_id}/`,
        { headers: { "X-Organization-Id": currentOrganization.organization_id } }
      );
      setOrganizationRaw(response.data);
      setIsDirty(false);
      if (response.data.tire_hotel_swap_job) {
        try {
          const job = await axios.get(`/api/jobs/${response.data.tire_hotel_swap_job}/`, {
            headers: { "X-Organization-Id": currentOrganization.organization_id },
          });
          setSwapJob(job.data);
        } catch (error) {
          setSwapJob(null);
        }
      } else {
        setSwapJob(null);
      }
    } catch (error) {
      console.error("Failed to fetch organization:", error);
      setSnackbar({ open: true, message: t("Failed to load organization data"), severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [currentOrganization?.organization_id, t]);

  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  useEffect(() => {
    const orgId = currentOrganization?.organization_id;
    if (!orgId) return undefined;
    let cancelled = false;
    axios
      .get("/api/service-packages/", {
        params: { is_active: true, page_size: 100 },
        headers: { "X-Organization-Id": orgId },
      })
      .then((response) => {
        if (cancelled) return;
        const data = response.data.results || response.data;
        setPackages(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setPackages([]);
      });
    return () => {
      cancelled = true;
    };
  }, [currentOrganization?.organization_id]);

  const setOrganization = useCallback((updater) => {
    setOrganizationRaw(updater);
    setIsDirty(true);
  }, []);

  const handleChange = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setOrganization((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!organization) return;
    try {
      setSaving(true);
      const payload = {};
      TIRE_HOTEL_FIELDS.forEach((field) => {
        payload[field] = organization[field];
      });
      await axios.patch(
        `/api/organizations/${currentOrganization.organization_id}/`,
        payload,
        {
          headers: {
            "X-CSRFToken": getCsrfToken(),
            "X-Organization-Id": currentOrganization.organization_id,
          },
        }
      );
      await fetchUser();
      await fetchOrganization();
      setSnackbar({ open: true, message: t("Organization updated successfully"), severity: "success" });
    } catch (error) {
      console.error("Failed to update tire hotel settings:", error);
      const message = error.response?.data?.detail || t("Failed to update organization");
      setSnackbar({ open: true, message, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  useEffect(() => {
    onDirtyChange?.(isDirty, () => handleSaveRef.current());
  }, [isDirty, onDirtyChange]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !organization) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={organization.tire_hotel_enabled || false}
                onChange={handleChange("tire_hotel_enabled")}
              />
            }
            label={t("tire_hotel.featureEnabled")}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            {t("tire_hotel.featureEnabledHelp")}
          </Typography>
        </Grid>

        {organization.tire_hotel_enabled && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600, color: "text.secondary" }}>
                {t("tire_hotel.settings_thresholds")}
              </Typography>
            </Grid>

            <Grid item xs={6} md={3}>
              <DecimalField
                fullWidth
                label={t("tire_hotel.tread_depth_warning_threshold")}
                value={organization.tire_hotel_tread_depth_warning ?? ""}
                onChange={(val) =>
                  setOrganization((prev) => ({ ...prev, tire_hotel_tread_depth_warning: val }))
                }
                maxDecimals={1}
                size="small"
                InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }}
                helperText={t("tire_hotel.tread_depth_warning_help")}
              />
            </Grid>

            <Grid item xs={6} md={3}>
              <DecimalField
                fullWidth
                label={t("tire_hotel.tread_depth_critical_threshold")}
                value={organization.tire_hotel_tread_depth_critical ?? ""}
                onChange={(val) =>
                  setOrganization((prev) => ({ ...prev, tire_hotel_tread_depth_critical: val }))
                }
                maxDecimals={1}
                size="small"
                InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }}
                helperText={t("tire_hotel.tread_depth_critical_help")}
              />
            </Grid>

            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label={t("tire_hotel.dot_age_warning_threshold")}
                type="number"
                value={organization.tire_hotel_dot_age_warning ?? ""}
                onChange={handleChange("tire_hotel_dot_age_warning")}
                size="small"
                InputProps={{
                  endAdornment: <InputAdornment position="end">{t("tire_hotel.years")}</InputAdornment>,
                }}
                helperText={t("tire_hotel.dot_age_warning_help")}
              />
            </Grid>

            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label={t("tire_hotel.dot_age_critical_threshold")}
                type="number"
                value={organization.tire_hotel_dot_age_critical ?? ""}
                onChange={handleChange("tire_hotel_dot_age_critical")}
                size="small"
                InputProps={{
                  endAdornment: <InputAdornment position="end">{t("tire_hotel.years")}</InputAdornment>,
                }}
                helperText={t("tire_hotel.dot_age_critical_help")}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("tire_hotel.capacity_alert_threshold")}
                type="number"
                value={organization.tire_hotel_capacity_alert_threshold ?? 90}
                onChange={handleChange("tire_hotel_capacity_alert_threshold")}
                size="small"
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                helperText={t("tire_hotel.capacity_alert_threshold_help")}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600, color: "text.secondary" }}>
                {t("tire_hotel.swap_billing_title")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("tire_hotel.swap_billing_help")}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <RadioGroup
                value={organization.tire_hotel_swap_billing_mode || "job"}
                onChange={handleChange("tire_hotel_swap_billing_mode")}
                data-testid="swap-billing-mode"
              >
                <FormControlLabel
                  value="none"
                  control={<Radio size="small" />}
                  label={t("tire_hotel.swap_billing_mode_none")}
                />
                <FormControlLabel
                  value="job"
                  control={<Radio size="small" />}
                  label={t("tire_hotel.swap_billing_mode_job")}
                />
                <FormControlLabel
                  value="package"
                  control={<Radio size="small" />}
                  label={t("tire_hotel.swap_billing_mode_package")}
                />
              </RadioGroup>
            </Grid>

            {(organization.tire_hotel_swap_billing_mode || "job") === "job" && (
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={jobOptions}
                  {...jobAutocompleteProps}
                  getOptionLabel={(opt) => opt.name || ""}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  value={swapJob}
                  onChange={(_, val) => {
                    setSwapJob(val);
                    setOrganization((prev) => ({ ...prev, tire_hotel_swap_job: val?.id || null }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      autoComplete="off"
                      label={t("tire_hotel.swap_billing_job_label")}
                      size="small"
                      helperText={t("tire_hotel.swap_billing_job_help")}
                      data-testid="swap-billing-job"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {jobAutocompleteProps.loading ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            )}

            {organization.tire_hotel_swap_billing_mode === "package" && (
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label={t("tire_hotel.swap_billing_package_label")}
                  value={organization.tire_hotel_swap_service_package ?? ""}
                  onChange={(event) =>
                    setOrganization((prev) => ({
                      ...prev,
                      tire_hotel_swap_service_package: event.target.value || null,
                    }))
                  }
                  size="small"
                  helperText={
                    packages.length
                      ? t("tire_hotel.swap_billing_package_help")
                      : t("tire_hotel.swap_billing_no_packages")
                  }
                  data-testid="swap-billing-package"
                >
                  <MenuItem value="">
                    <em>{t("tire_hotel.swap_billing_package_none")}</em>
                  </MenuItem>
                  {packages.map((pkg) => (
                    <MenuItem key={pkg.id} value={pkg.id}>
                      {pkg.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600, color: "text.secondary" }}>
                {t("tire_hotel.settings_label_printing")}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label={t("tire_hotel.label_orientation")}
                value={organization.tire_hotel_label_orientation || "landscape"}
                onChange={handleChange("tire_hotel_label_orientation")}
                size="small"
                helperText={t("tire_hotel.label_orientation_help")}
                data-testid="label-orientation"
              >
                <MenuItem value="landscape">{t("tire_hotel.label_orientation_landscape")}</MenuItem>
                <MenuItem value="portrait">{t("tire_hotel.label_orientation_portrait")}</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600, color: "text.secondary" }}>
                {t("tire_hotel.settings_contact_phone")}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("tire_hotel.contact_phone_label")}
                value={organization.tire_hotel_contact_phone ?? ""}
                onChange={handleChange("tire_hotel_contact_phone")}
                size="small"
                type="tel"
                inputProps={{ maxLength: 20, "data-testid": "tire-hotel-contact-phone" }}
                helperText={[
                  t("tire_hotel.contact_phone_help"),
                  organization.phone ? t("tire_hotel.contact_phone_fallback", { phone: organization.phone }) : null,
                ]
                  .filter(Boolean)
                  .join(" ")}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600, color: "text.secondary" }}>
                {t("tire_hotel.settings_campaign_templates")}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("tire_hotel.campaign_email_subject_label")}
                value={organization.tire_hotel_campaign_email_subject ?? ""}
                onChange={handleChange("tire_hotel_campaign_email_subject")}
                size="small"
                helperText={`${organization.name}: ${organization.tire_hotel_campaign_email_subject || "..."}`}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("tire_hotel.campaign_email_template_label")}
                multiline
                rows={6}
                value={organization.tire_hotel_campaign_email_template ?? ""}
                onChange={handleChange("tire_hotel_campaign_email_template")}
                size="small"
                helperText={t("tire_hotel.campaign_template_variables_help")}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("tire_hotel.campaign_sms_template_label")}
                multiline
                rows={3}
                value={organization.tire_hotel_campaign_sms_template ?? ""}
                onChange={handleChange("tire_hotel_campaign_sms_template")}
                size="small"
                helperText={t("tire_hotel.campaign_template_variables_help")}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={() => setPreviewOpen(true)}
                data-testid="tire-hotel-campaign-preview-button"
              >
                {t("tire_hotel.campaign_preview_button")}
              </Button>
              <MessagePreviewDialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                title={t("tire_hotel.campaign_preview_title")}
                loadPreview={loadCampaignPreview}
                showLanguage={false}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={saving || !isDirty}>
              {saving ? <CircularProgress size={20} /> : <span>{t("Save Changes")}</span>}
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default withTranslation()(TireHotelSettingsTab);
